"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { AdminSidebar } from "./admin/sidebar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu } from "lucide-react";
import { useContentLayout } from "@/lib/contexts/content-layout-context";
import { PageTopBar } from "./page-top-bar";
import { cn } from "@/lib/utils";

interface LayoutWithSidebarProps {
  children: React.ReactNode;
}

export function LayoutWithSidebar({ children }: LayoutWithSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathname = usePathname();
  const { isExpanded } = useContentLayout();

  // Don't show sidebar on auth pages (sign-in, sign-up)
  const isAuthPage =
    pathname?.startsWith("/sign-in") || pathname?.startsWith("/sign-up");

  if (isAuthPage) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar - width is managed by the sidebar component itself */}
      <aside className="hidden lg:flex lg:flex-col">
        <AdminSidebar />
      </aside>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile Header with Menu Button */}
        <header className="flex h-16 items-center gap-4 border-b bg-background px-4 lg:hidden">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle sidebar</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <AdminSidebar onNavigate={() => setSidebarOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold text-foreground">Omizi WMS</span>
          </div>
        </header>

        {/* Page Top Bar - Always visible */}
        <PageTopBar />

        {/* Main content with scrolling */}
        <main className="flex-1 overflow-y-auto bg-background">
          {/* Content wrapper with expand/collapse support */}
          <div
            className={cn(
              "transition-all duration-300",
              isExpanded ? "w-full" : "max-w-7xl mx-auto"
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
